/* eslint-disable react-hooks/exhaustive-deps */
import { Box } from "@chakra-ui/layout"
import dayjs from "dayjs"
import React, { useState, useRef, useEffect, useCallback } from "react"
import {
  useRecoilState,
  useRecoilValue
  // useSetRecoilState
} from "recoil"
import useChatNotification from "../../../hooks/useChatNotification"
import {
  // bottomMostUnreadAtom,
  // chatIsReadAtom,
  chatScrollStateAtom,
  lastReactionAtom
} from "../../../recoil/atoms/chats"
import { ChatModel } from "../../../recoil/atoms/chats/types"
import layoutStates from "../../../recoil/atoms/layout"
import { localUserStaticPropsAtom } from "../../../recoil/atoms/users"
import { waitForElm } from "../../../util/waitForElm"
import HandleNewReplyMessage from "./HandleNewReplyMessage"
import advancedFormat from 'dayjs/plugin/advancedFormat'
import useAdvancedChatScroller from "./utils/useAdvancedChatScroller"

dayjs.extend(advancedFormat)

type VirtualizedListProps = {
  list: ChatModel[]
  scrollElementId: string
  renderItem: (item: ChatModel, index?: number) => React.ReactNode
  renderDaySplit: (date: string) => React.ReactNode
  isMobile?: boolean
  onScrollCallback?: () => void
}

export const NO_ITEM_TO_LOAD = 20
const MAX_ITEM_TO_DISPLAY = 100
const SCROLL_THRESHOLD = 300

// move to utils
const elementVisible = (containerEle: HTMLElement, innerEle: HTMLElement) => {
  if (
    containerEle?.getBoundingClientRect().top <
      innerEle?.getBoundingClientRect().top &&
    containerEle?.getBoundingClientRect().bottom >
      innerEle?.getBoundingClientRect().bottom
  ) {
    return true
  }
  return false
}

const atBottom = (ele: HTMLElement) => {
  const sh = ele.scrollHeight
  const st = ele.scrollTop
  const ht = ele.offsetHeight
  if (ht === 0) {
    return true
  }
  if (st === sh - ht) {
    return true
  } else {
    return false
  }
}

const handleScrollToChat = async (id: string, onSuccess) => {
  let timer = setTimeout(async () => {
    const nestedElement = (await waitForElm(id)) as HTMLDivElement
    nestedElement.scrollIntoView({ block: "center", behavior: "smooth" })
    onSuccess()
  }, 300)

  return () => clearInterval(timer)
}

const _ChatVirtualizedList = ({
  list,
  renderItem,
  renderDaySplit,
  scrollElementId,
  isMobile,
  onScrollCallback
}: VirtualizedListProps) => {
  // Message to be displayed
  const [messages, setMessages] = useState(
    list.slice(Math.max(list.length - NO_ITEM_TO_LOAD, 0))
  )
  const localUserProps = useRecoilValue(localUserStaticPropsAtom)
  const [chatScrollState, setChatScrollState] =
    useRecoilState(chatScrollStateAtom)
  const isChatTab = useRecoilValue<boolean>(layoutStates.isChatTab)
  const lastReaction = useRecoilValue(lastReactionAtom)
  // const newMessages = useRecoilValue(newMessagesAtom)
  const isChatTabRef = useRef<boolean>()
  isChatTabRef.current = isChatTab

  useAdvancedChatScroller(scrollElementId)

  const {
    setBottomMostMessage,
    setTopMostMessage,
    setNewChatMessage,
    bottomMostUnreadRef,
    topMostUnreadRef
  } = useChatNotification()
  // const setChatIsRead = useSetRecoilState(chatIsReadAtom)

  // Indexing
  const startIndexRef = useRef<number>(list.length - messages.length)
  const endIndexRef = useRef<number>(list.length)
  const messageLengthRef = useRef(messages.length)

  // has more
  const hasNextItemRef = useRef(false)
  const hasPrevItemRef = useRef(messages.length !== list.length)

  //
  const isFetchingRef = useRef<boolean>(false)
  // const isFetchingNextRef = useRef<boolean>(false)

  const messagesContainerRef = useRef(null)

  const fetchItemsCallback =
    useRef<(mode: "prev" | "next") => Promise<void>>(null)
  const lastScrollTopValueRef = useRef<number>(0)
  const listPrevLengthRef = useRef(list.length)
  const listRef = useRef(list)

  const updateMessageList = useCallback(
    (startIndex: number, endIndex: number) => {
      startIndex = Math.max(0, startIndex)
      endIndex = Math.min(listRef.current.length, endIndex)

      startIndexRef.current = startIndex
      endIndexRef.current = endIndex

      const newItems = listRef.current.slice(startIndex, endIndex)
      messageLengthRef.current = newItems.length
      hasPrevItemRef.current = startIndex !== 0
      hasNextItemRef.current = endIndex !== listRef.current.length

      setMessages(newItems)
      // setChatIsRead(true)
      setBottomMostMessage(null)
    },
    []
  )

  const addItemToList = React.useCallback(
    (list, mode: "prev" | "next", count: number = 20) => {
      if (isFetchingRef.current) {
        return
      }
      isFetchingRef.current = true
      let newItems = []
      let startIndex = 0
      let endIndex = 0

      if (mode === "prev") {
        startIndex = Math.max(0, startIndexRef.current - count)
        endIndex = list.length
        if (endIndex - startIndex > MAX_ITEM_TO_DISPLAY) {
          endIndex = startIndex + MAX_ITEM_TO_DISPLAY
          if (!bottomMostUnreadRef.current) {
            setNewChatMessage(true) // enabling down arrow
          }
        }
      } else {
        // startIndexRef.current, startIndexRef.current + prevItem.length + 10
        startIndex = startIndexRef.current
        endIndex = Math.min(list.length, endIndexRef.current + count)
        if (endIndex - startIndex > MAX_ITEM_TO_DISPLAY) {
          startIndex = endIndex - MAX_ITEM_TO_DISPLAY
        }
      }

      startIndexRef.current = startIndex
      endIndexRef.current = endIndex
      messageLengthRef.current = endIndex - startIndex

      newItems = list.slice(startIndex, endIndex)
      hasPrevItemRef.current = startIndex !== 0
      hasNextItemRef.current = endIndex !== list.length
      if (hasNextItemRef.current) {
        // Show Down Arrow
        // setBottomMostUnread({ type: "Reply", parentId: -1, id: 0 })
        setNewChatMessage(true)
      }
      setMessages(newItems)
      isFetchingRef.current = false
    },
    []
  )

  const handleScrollToBottomChat = React.useCallback(async () => {
    // setChatIsRead(true)
    setBottomMostMessage(null)
    let timer = setTimeout(async () => {
      const nestedElement = (await waitForElm(
        scrollElementId
      )) as HTMLDivElement
      nestedElement.scrollTo(0, nestedElement.scrollHeight)
      setBottomMostMessage(null)
    }, 300)

    return () => clearInterval(timer)
  }, [])

  fetchItemsCallback.current = React.useCallback(
    async (mode: "prev" | "next" = "prev") => {
      if (mode === "prev") {
        addItemToList(list, mode)
      } else {
        addItemToList(list, mode)
      }
    },
    [list, addItemToList]
  )

  const hideUpDownArrow = React.useCallback((arrow: "UP" | "DOWN") => {
    const messagesContainer = document.getElementById(scrollElementId)
    let replyContainer = null
    if (arrow === "DOWN" && bottomMostUnreadRef.current) {
      replyContainer = document.getElementById(
        `reply-${bottomMostUnreadRef.current.id}`
      )
      if (replyContainer && elementVisible(messagesContainer, replyContainer)) {
        // setChatIsRead(true)
        setBottomMostMessage(null)
      }
    }

    if (arrow === "UP" && topMostUnreadRef.current) {
      replyContainer = document.getElementById(
        `reply-${topMostUnreadRef.current.id}`
      )
      if (replyContainer && elementVisible(messagesContainer, replyContainer)) {
        // setChatIsRead(true)
        setTopMostMessage(null)
      }
    }
  }, [])

  const getParentIdDirection = useCallback(parentChatId => {
    for (let i = 0; i < listRef.current.length; i++) {
      if (listRef.current[i].id === parentChatId) {
        return "UP"
      } else if (
        listRef.current[i].id === listRef.current[startIndexRef.current].id
      ) {
        return "DOWN"
      }
    }
  }, [])

  const checkWhoComeFirst = useCallback(
    (parentId1: string | number, parentId2: string | number) => {
      if (!parentId1) return parentId2
      if (!parentId2) return parentId1

      if (parentId2 === -1) return parentId2
      if (parentId1 === -1) return parentId1

      for (let i = 0; i < listRef.current.length; i++) {
        if (listRef.current[i].id === parentId1) {
          return parentId1
        } else if (listRef.current[i].id === parentId2) {
          return parentId2
        }
      }
      return 0
    },
    []
  )

  const isFirstLoad = useRef(true)
  useEffect(() => {
    if (isFirstLoad.current && isChatTab) {
      isFirstLoad.current = false
      const messagesContainer = document.getElementById(scrollElementId)
      messagesContainerRef.current = messagesContainer
      // if(listPrevLengthRef.current > 1) {
      // Scroll to button on first load
      handleScrollToBottomChat()
      // }
      const handleScroll = ev => {
        const scrollTop = ev.target.scrollTop
        const isScrollUp = lastScrollTopValueRef.current > scrollTop
        const isScrollDown = lastScrollTopValueRef.current < scrollTop
        lastScrollTopValueRef.current = scrollTop
        const bottomZone =
          messagesContainer?.scrollTop >=
          messagesContainer?.scrollHeight -
            messagesContainer?.clientHeight -
            (isMobile ? 100 : 400)

        if (onScrollCallback) {
          onScrollCallback()
        }

        if (
          scrollTop <= SCROLL_THRESHOLD &&
          !isFetchingRef.current &&
          hasPrevItemRef.current &&
          isScrollUp
        ) {
          // fetch prev batch
          fetchItemsCallback.current?.("prev")
        }

        if (bottomZone && isScrollDown && hasNextItemRef.current) {
          // fetch next batch
          fetchItemsCallback.current?.("next")
        }

        if (atBottom(ev.target)) {
          // Hide Down Arrow
          setBottomMostMessage(null)
          // setChatIsRead(true)
        }

        if (isScrollDown) hideUpDownArrow("DOWN")

        if (isScrollUp) hideUpDownArrow("UP")
      }

      messagesContainer?.addEventListener("scroll", handleScroll, {
        passive: true
      })
      return () => messagesContainer.removeEventListener("scroll", handleScroll)
    }
  }, [scrollElementId, isChatTab])

  // const prevNewMessageId = useRef(null)

  useEffect(() => {
    const numOfNewMessage = list.length - listPrevLengthRef.current
    // const isNewMessage = newMessages.length === 0 ? false : newMessages[0] !== prevNewMessageId.current;
    // prevNewMessageId.current = newMessages.length !== 0 ? newMessages[0] : null
    if (!isChatTab) {
      if (numOfNewMessage > 0) {
        setNewChatMessage()
      }
      return
    }
    listRef.current = list
    const messagesContainer = document.getElementById(scrollElementId)

    if (numOfNewMessage > 0) {
      const bottomZone =
        messagesContainer?.scrollTop >=
        messagesContainer?.scrollHeight -
          messagesContainer?.clientHeight -
          (isMobile ? 100 : 400)

      if (bottomZone) {
        // Inside scroll area
        addItemToList(list, "next", numOfNewMessage)
        if (numOfNewMessage < 10) {
          handleScrollToBottomChat()
        } else {
          setNewChatMessage()
          setTimeout(() => {
            messagesContainer.scrollTo(0, messagesContainer.scrollTop + 100)
          }, 100)
        }
      } else {
        if (messageLengthRef.current < MAX_ITEM_TO_DISPLAY) {
          addItemToList(list, "next")
        }
        setNewChatMessage()
        hasNextItemRef.current = true
      }

      if (
        !bottomZone &&
        list
          .slice(listPrevLengthRef.current)
          .find(item => item.author?.id === localUserProps.id)
      ) {
        // Scroll Down If message send by local user
        if (numOfNewMessage < 10) {
          setNewChatMessage()
          setChatScrollState("DOWN")
        } else {
          setNewChatMessage()
          setTimeout(() => {    
            messagesContainer.scrollTo(0, messagesContainer.scrollTop + 100)
          }, 100)
        }
      }
    }
    listPrevLengthRef.current = list.length
  }, [list, isChatTab])

  useEffect(() => {
    if (!isChatTab) return
    // Handle Arrow Scrolling
    let startIndex = 0
    let endIndex = 0
    if (chatScrollState !== "DONE") {
      if (chatScrollState === "DOWN") {
        if (!bottomMostUnreadRef.current) {
          return
        }
        if (bottomMostUnreadRef.current?.parentId === -1) {
          setBottomMostMessage(null)
          startIndex = listRef.current.length - MAX_ITEM_TO_DISPLAY
          endIndex = listRef.current.length
          updateMessageList(startIndex, endIndex)
          handleScrollToBottomChat()
        } else {
          const isPresent = listRef.current
            ?.slice(startIndexRef.current, endIndexRef.current)
            .find(value => value.id === bottomMostUnreadRef.current?.parentId)
          if (!isPresent) {
            const position = listRef.current.findIndex(
              value => value.id === bottomMostUnreadRef.current?.parentId
            )
            const difference = position - endIndexRef.current
            const itemToAdd = difference + 5

            updateMessageList(
              startIndexRef.current + itemToAdd,
              endIndexRef.current + itemToAdd
            )
          }

          handleScrollToChat(`reply-${bottomMostUnreadRef.current?.id}`, () => {
            setBottomMostMessage(null)
          })
        }
      } else {
        if (topMostUnreadRef.current === null) {
          return
        }
        const isPresent = listRef.current
          ?.slice(startIndexRef.current, endIndexRef.current)
          .find(value => value.id === topMostUnreadRef.current.parentId)
        if (!isPresent) {
          const position = listRef.current.findIndex(
            value => value.id === topMostUnreadRef.current.parentId
          )
          const difference = startIndexRef.current - position
          const itemToAdd = difference + 5

          updateMessageList(
            startIndexRef.current - itemToAdd,
            endIndexRef.current - itemToAdd
          )
        }

        handleScrollToChat(`reply-${topMostUnreadRef.current.id}`, () => {
          setTopMostMessage(null)
        })
      }
      setChatScrollState("DONE")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatScrollState, isChatTab])

  useEffect(() => {
    const messagesContainer = document.getElementById(scrollElementId)
    const bottomZone =
      messagesContainer?.scrollTop >=
      messagesContainer?.scrollHeight - messagesContainer?.clientHeight - 100
    const lastMessage = messages[messages.length - 1]
    if (
      bottomZone &&
      lastReaction.type === "message" &&
      lastMessage.id === lastReaction.id
    ) {
      handleScrollToBottomChat()
    }

    if (
      bottomZone &&
      lastReaction.type === "reply" &&
      lastMessage.id === lastReaction?.parentChatId
    ) {
      const lastReply =
        lastMessage.replyChats[lastMessage.replyChats.length - 1]
      if (lastReply?.id === lastReaction?.id) {
        handleScrollToBottomChat()
      }
    }
  }, [lastReaction])

  function isSameDay(d1: Date, d2: Date) {
    return (
      d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear()
    )
  }

  function newDayMessage(messages: ChatModel[], i: number) {
    if (i > 0) {
      const d1 = new Date(messages[i - 1].timestamp)
      const d2 = new Date(messages[i].timestamp)

      if (
        new Date(messages[i].timestamp).getDate() === new Date().getDate() &&
        !isSameDay(d1, d2)
      ) {
        return renderDaySplit("Today")
      }
        
      if (
        new Date().getDate() === dayjs(d2).subtract(1, "day").toDate().getDate() &&
        !isSameDay(d1, d2)
      ) {
        return renderDaySplit("Yesterday")
      }

      if (
        d1.getTime() < d2.getTime() && !isSameDay(d1, d2)
      ) {
        const formattedDate = dayjs(messages[i].timestamp).format('dddd Do MMMM')
        return renderDaySplit(formattedDate)
      }
    }
    return null
  }

  return (
    <>
      {messages.map((message, i) => {
        return (
          <React.Fragment key={message.id}>
            {newDayMessage(messages, i)}
            {renderItem(message)}
          </React.Fragment>
        )
      })}
      <HandleNewReplyMessage
        isMobile={isMobile}
        scrollElementId={scrollElementId}
        getParentIdDirection={getParentIdDirection}
        checkWhoComeFirst={checkWhoComeFirst}
        handleScrollToBottomChat={handleScrollToBottomChat}
      />
    </>
  )
}

const ChatVirtualizedList = React.memo(_ChatVirtualizedList)
export default ChatVirtualizedList
